import { createContext } from 'react';
import { action, observable, decorate } from 'mobx';
import { i18n } from '@lingui/core';
import { en, fr, de, ja, zh, es, it, ru, pt, nl } from 'make-plural/plurals';

import { UserAgent } from 'agents';
import { setLocale } from 'services/dayjs';
import { messages } from 'locales/en/messages';

type Locale =
    | 'en'
    | 'fr'
    | 'de'
    | 'ja'
    | 'it'
    | 'nl'
    | 'ru'
    | 'pt-BR'
    | 'zh-Hans'
    | 'zh-Hant'
    | 'es-419'
    | 'pseudo-LOCALE';

type Files =
    | 'en'
    | 'fr'
    | 'de'
    | 'ja'
    | 'it'
    | 'nl'
    | 'ru'
    | 'pt_BR'
    | 'zh-Hans'
    | 'zh-Hant'
    | 'es_419'
    | 'pseudo-LOCALE';

// TODO put somewhere else, and add other keys
interface User {
    locale?: string;
}

const defaultLocale: Locale = 'en';

i18n.loadLocaleData({
    en: { plurals: en },
    fr: { plurals: fr },
    de: { plurals: de },
    ja: { plurals: ja },
    it: { plurals: it },
    nl: { plurals: nl },
    ru: { plurals: ru },
    'pt-BR': { plurals: pt },
    'zh-Hans': { plurals: zh },
    'zh-Hant': { plurals: zh },
    'es-419': { plurals: es },
    'pseudo-LOCALE': { plurals: en },
});

// load & activate initial english
i18n.load(defaultLocale, messages);
i18n.activate(defaultLocale);

class LocalesStore {
    readonly availableLocales: Locale[] = [
        'en',
        'fr',
        'de',
        'es-419',
        'ja',
        'it',
        'nl',
        'pt-BR',
        'ru',
        'zh-Hans',
        'zh-Hant',
        'pseudo-LOCALE',
    ];
    readonly chineseLocalesMap: { [unit: string]: Locale } = {
        'zh-cn': 'zh-Hans',
        'zh-tw': 'zh-Hant',
        'zh-hk': 'zh-Hant',
        'zh-sg': 'zh-Hans',
        zh: 'zh-Hans',
    };

    readonly availableFiles: Files[] = [
        'en',
        'fr',
        'de',
        'es_419',
        'ja',
        'it',
        'nl',
        'pt_BR',
        'ru',
        'pseudo-LOCALE',
        'zh-Hans',
        'zh-Hant',
    ];

    locale: Locale = defaultLocale;
    pseudo_locale: boolean = false;

    normalizeLocale(locale: string): string {
        return locale.replace('_', '-').toLowerCase();
    }

    denormalizeLocale(locale: string): string {
        return locale.replace('-', '_');
    }

    localeToLanguage(locale: string): string {
        // Return the language part of a locale: es-419 => es
        return locale.split(/[-_]/)[0].toLowerCase();
    }

    getUsableLocale(locale: string): Locale | undefined {
        locale = this.normalizeLocale(locale);
        if (this.chineseLocalesMap[locale]) {
            return this.chineseLocalesMap[locale];
        }
        return this.availableLocales.find(
            (availableLocale) =>
                this.normalizeLocale(availableLocale) === locale,
        );
    }

    getUsableLanguage(locale: string): Locale | undefined {
        const language = this.localeToLanguage(locale);
        return this.availableLocales.find(
            (availableLocale) =>
                this.localeToLanguage(availableLocale) === language,
        );
    }

    getUsableLang(locale: string): Locale | undefined {
        return this.getUsableLocale(locale) || this.getUsableLanguage(locale);
    }

    getMatchingLocale(
        locale: string[] | string | undefined,
    ): Locale | undefined {
        if (!locale) return;
        if (Array.isArray(locale)) {
            for (let i = 0; i < locale.length; i++) {
                const usableLocale = this.getUsableLang(locale[i]);
                if (usableLocale) return usableLocale;
            }
        } else {
            return this.getUsableLang(locale);
        }
    }

    getNavigatorLocales(): string[] | undefined {
        if (!navigator) return;
        const locales: string[] = [];
        if (Array.isArray(navigator.languages))
            locales.push(...navigator.languages);
        if (navigator.language) locales.push(navigator.language);
        if (locales.length > 0) return locales;
    }

    getLocale(
        user: User | undefined,
        queryStringLocale: string[] | string | undefined,
    ): Locale {
        if (this.pseudo_locale) {
            return 'pseudo-LOCALE';
        }
        return (
            this.getMatchingLocale(user?.locale) ||
            this.getMatchingLocale(queryStringLocale) ||
            this.getMatchingLocale(this.getNavigatorLocales()) ||
            defaultLocale
        );
    }

    async setUserLocale(locale: string[] | string | undefined) {
        return UserAgent.locale(locale);
    }

    async setupLocale(user?: User, queryStringLocal?: string[] | string) {
        const newLocale = this.getLocale(user, queryStringLocal);
        if (newLocale === this.locale) return;

        const denormalized_locale = this.denormalizeLocale(newLocale);

        const file = this.availableFiles.find(
            (file) => file === newLocale || file === denormalized_locale,
        );

        const { messages } = await import(`locales/${file}/messages.js`);

        i18n.load(newLocale, messages);
        i18n.activate(newLocale);

        this.locale = newLocale;

        // Set locale for dates
        if (newLocale === 'es-419') {
            setLocale('es');
        } else if (newLocale === 'zh-Hans' || newLocale === 'zh-Hant') {
            setLocale('zh-cn');
        } else if (newLocale === 'pt-BR') {
            setLocale('pt-br');
        } else {
            setLocale(newLocale);
        }
    }
}

decorate(LocalesStore, {
    locale: observable,
    setupLocale: action,
});

export const localesStore = new LocalesStore();

export default createContext(localesStore);
